import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import * as transportActions from '../../actions/transport';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import { toast } from 'react-toastify';
import '../Contact/Contact.scss';

const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

interface Props {
    currentTransport: any;
    availableItems: any[];
    loading: boolean;
    actions: any;
    history: any;
    match: any;
}

const TransportForm: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    const transportId = props.match.params.transportId ? parseInt(props.match.params.transportId) : null;
    const isEdit = !!transportId;

    const currentUsername = localStorage.getItem('username') || '';

    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [comments, setComments] = useState<string[]>([]);
    const [newComment, setNewComment] = useState('');
    const [itemSearch, setItemSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [deponimetExpanded, setDeponimetExpanded] = useState(true);

    useEffect(() => {
        if (!isEdit) props.actions.fetchAvailableItems('', 1, 100);
        if (isEdit && transportId) props.actions.fetchTransport(transportId);
    }, []);

    // Pre-select WR from URL param
    useEffect(() => {
        if (!isEdit) {
            const params = new URLSearchParams(props.history.location.search);
            const wrId = params.get('wr');
            if (wrId) {
                setSelectedItems([parseInt(wrId)]);
            }
        }
    }, []);

    // Edit mode populate

    const handleItemSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setItemSearch(e.target.value);
        props.actions.fetchAvailableItems(e.target.value, 1, 100);
    };

    const toggleItem = (id: number) => {
        setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const addComment = () => {
        if (newComment.trim()) {
            setComments(prev => [...prev, newComment.trim()]);
            setNewComment('');
        }
    };

    // Group selected items by project
    const deliveryGroups = useMemo(() => {
        const selected = (props.availableItems || []).filter((item: any) => selectedItems.includes(item.id));
        const groups: Record<string, { project: any; client: any; measureLocation: string | null; items: any[] }> = {};

        selected.forEach((item: any) => {
            const projectId = item.project?.id || 'no-project';
            const key = `${projectId}-${item.client?.id || 'no-client'}`;

            if (!groups[key]) {
                groups[key] = {
                    project: item.project,
                    client: item.client,
                    measureLocation: item.measureLocation,
                    items: [],
                };
            }
            groups[key].items.push(item);
        });

        return Object.values(groups);
    }, [selectedItems, props.availableItems]);

    const handleSubmit = async () => {
        if (!isEdit && selectedItems.length === 0) {
            toast.error(fm('transport.form.toast.selectAtLeastOne', 'Zgjidhni të paktën një deponim'));
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await props.actions.updateTransport(transportId, {});
                toast.success(fm('transport.form.toast.transportUpdated', 'Transporti u përditësua'));
                props.history.push(`/dashboard/transport/${transportId}`);
            } else {
                const result = await props.actions.createTransport({
                    warehouse_record_ids: selectedItems,
                    comments: comments.length > 0 ? comments : undefined,
                });
                toast.success(fm('transport.form.toast.transportCreated', 'Transporti u krijua'));
                if (result?.data?.id) props.history.push(`/dashboard/transport/${result.data.id}`);
                else props.history.push('/dashboard/transport?filter=inTransit');
            }
        } catch (err) {
            toast.error(fm('transport.form.toast.somethingWrong', 'Diçka shkoi gabim'));
        }
        setSaving(false);
    };

    const parseCoords = (loc: string | null) => {
        if (!loc) return null;
        const parts = loc.split(',').map(s => parseFloat(s.trim()));
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return parts;
        return null;
    };

    return (
        <div className="clp-page" style={{ gap: 12 }}>
            <MikaPageHeader
                title={isEdit ? fm('transport.form.editTitle', 'Modifiko Transportin') : fm('transport.form.createTitle', 'Krijo Transport të Ri')}
                subtitle={isEdit
                    ? fm('transport.form.codeSubtitle', 'Kodi: {code}', { code: props.currentTransport?.code?.substring(0, 8) || '' })
                    : fm('transport.form.selectedSubtitle', '{count} deponime të zgjedhura', { count: selectedItems.length })}
                actions={
                    <button onClick={handleSubmit} disabled={saving}
                        style={{
                            padding: '7px 18px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)',
                            background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 12, fontWeight: 500,
                            cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1,
                            display: 'flex', alignItems: 'center', gap: 6, transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget).style.background = 'rgba(255,255,255,0.22)'; }}
                        onMouseLeave={(e) => { (e.currentTarget).style.background = 'rgba(255,255,255,0.12)'; }}
                    >
                        {saving
                            ? fm('production.statusForm.saving', 'Duke ruajtur...')
                            : (isEdit ? fm('transport.form.saveChanges', 'Ruaj ndryshimet') : fm('transport.form.createAction', 'Krijo transportin'))}
                    </button>
                }
            />

            <div className="t-form-row" style={{ flex: 1, overflow: 'auto', display: 'flex', gap: 12 }}>
                {/* Left column */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>

                    {/* Delivery cards — grouped by project */}
                    {deliveryGroups.map((group, idx) => {
                        const coords = parseCoords(group.measureLocation);
                        const googleMapsUrl = coords
                            ? `https://www.google.com/maps/dir/?api=1&destination=${coords[0]},${coords[1]}`
                            : null;
                        const totalProducts = group.items.reduce((sum: number, item: any) => sum + (item.products?.length || 0), 0);

                        return (
                            <div key={idx}>
                                <div className="t-card-header">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                                    {deliveryGroups.length > 1
                                        ? fm('transport.form.deliveryN', 'Dorëzimi {n}', { n: idx + 1 })
                                        : fm('transport.form.delivery', 'Dorëzimi')}
                                    <span className="t-card-header-badge">{fm('transport.card.productsCount', '{count} produkte', { count: totalProducts })}</span>
                                </div>
                                <div className="t-card-body">
                                    <div className="t-delivery-info">
                                        <div className="t-delivery-field">
                                            <div className="t-delivery-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></div>
                                            <div className="t-delivery-value">{group.client?.firstName || '-'} {group.client?.lastName || ''}</div>
                                        </div>
                                        <div className="t-delivery-field">
                                            <div className="t-delivery-label"><FormattedMessage id="transport.form.phone" defaultMessage="Telefoni" /></div>
                                            <div className="t-delivery-value">{group.client?.phone || <span className="t-delivery-value-empty">—</span>}</div>
                                        </div>
                                        <div className="t-delivery-field">
                                            <div className="t-delivery-label"><FormattedMessage id="transport.form.location" defaultMessage="Lokacioni" /></div>
                                            <div className="t-delivery-value">
                                                {googleMapsUrl ? (
                                                    <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="t-maps-link">
                                                        <svg width="11" height="14" viewBox="3 1 18 21" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg><FormattedMessage id="transport.form.googleMaps" defaultMessage="Google Maps" />
                                                    </a>
                                                ) : <span className="t-delivery-value-empty"><FormattedMessage id="transport.form.noLocation" defaultMessage="Pa lokacion" /></span>}
                                            </div>
                                        </div>
                                        <div className="t-delivery-field">
                                            <div className="t-delivery-label"><FormattedMessage id="transport.col.city" defaultMessage="Qyteti" /></div>
                                            <div className="t-delivery-value">{group.client?.city || <span className="t-delivery-value-empty">—</span>}</div>
                                        </div>
                                        <div className="t-delivery-field">
                                            <div className="t-delivery-label"><FormattedMessage id="transport.col.country" defaultMessage="Shteti" /></div>
                                            <div className="t-delivery-value">{group.client?.country || <span className="t-delivery-value-empty">—</span>}</div>
                                        </div>
                                        <div className="t-delivery-field">
                                            <div className="t-delivery-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></div>
                                            <div className="t-delivery-value">{group.project?.name || <span className="t-delivery-value-empty">—</span>}</div>
                                        </div>
                                    </div>

                                    <div className="t-products-section">
                                        {group.items.map((item: any) => (
                                            <div key={item.id} style={{ marginBottom: 10 }}>
                                                <div className="t-wr-header">
                                                    <span className="t-wr-label">{item.label || `WR-${item.id}`}</span>
                                                    <span className="t-wr-badge">{fm('transport.card.productsCount', '{count} produkte', { count: item.products?.length || 0 })}</span>
                                                </div>
                                                {(item.products || []).length > 0 ? (
                                                    item.products.map((p: any, pIdx: number) => (
                                                        <div key={pIdx} className="t-product-row">
                                                            <span className="t-product-name">{p.productName || p.articleName || fm('transport.form.unnamed', 'Pa emër')}</span>
                                                            {p.type && <span className="t-product-type">{p.type}</span>}
                                                            <span className="t-product-qty">{p.quantity ? `${p.quantity} ${p.unit || ''}`.trim() : '-'}</span>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="t-product-empty"><FormattedMessage id="transport.form.noProducts" defaultMessage="Pa produkte" /></div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Empty state when no items selected */}
                    {!isEdit && selectedItems.length === 0 && (
                        <div style={{
                            background: '#f8fafc', border: '2px dashed #d0dbe8', borderRadius: 8,
                            padding: '40px 20px', textAlign: 'center', color: '#94a3b8',
                        }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d0dbe8" strokeWidth="1.5" style={{ marginBottom: 8 }}>
                                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                            </svg>
                            <div style={{ fontSize: 13 }}><FormattedMessage id="transport.form.emptyState" defaultMessage="Zgjidhni deponime nga lista për të krijuar transportin" /></div>
                        </div>
                    )}

                    {/* Comments */}
                    {!isEdit && (
                        <div>
                            <div className="t-card-header">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                <FormattedMessage id="transport.form.comments" defaultMessage="Komentet" />
                            </div>
                            <div className="t-card-body">
                                {comments.map((c, idx) => (
                                    <div key={idx} style={{
                                        padding: '8px 10px', background: '#f8fafc', borderRadius: 6, marginBottom: 6,
                                        borderLeft: '3px solid #2a5a8a',
                                    }}>
                                        <div style={{ fontSize: 13, color: '#0f172a' }}>{c}</div>
                                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{currentUsername} — {fm('transport.form.now', 'tani')}</div>
                                    </div>
                                ))}
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <textarea
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                                        placeholder={fm('transport.form.commentPlaceholder', 'Shkruaj një koment...')}
                                        style={{ ...inputStyle, flex: 1, minHeight: 40, resize: 'vertical' }}
                                    />
                                    <button onClick={addComment} style={{
                                        padding: '8px 14px', borderRadius: 6, border: 'none', alignSelf: 'flex-end',
                                        background: '#1e3a5f', color: '#fff', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' as any,
                                    }}><FormattedMessage id="transport.form.addComment" defaultMessage="Shto" /></button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right column — Item selector (create mode only) */}
                {!isEdit && (
                    <div className="t-form-right-col" style={{ width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                        <div
                            className="t-card-header"
                            style={{
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                userSelect: 'none',
                                // When the body is hidden, round all 4 corners so the header
                                // doesn't look amputated. When expanded, defer to the SCSS
                                // rule (`8px 8px 0 0`) that joins the header to the body.
                                borderRadius: deponimetExpanded ? undefined : 8,
                            }}
                            onClick={() => setDeponimetExpanded(prev => !prev)}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                                <FormattedMessage id="transport.form.warehouseRecords" defaultMessage="Deponimet" />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {selectedItems.length > 0 && (
                                    <span className="t-card-header-badge">{selectedItems.length}</span>
                                )}
                                <svg
                                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2.5"
                                    style={{
                                        transition: 'transform 0.2s',
                                        transform: deponimetExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                >
                                    <polyline points="6 9 12 15 18 9"/>
                                </svg>
                            </div>
                        </div>
                        {deponimetExpanded && (
                        <div className="t-card-body" style={{ display: 'flex', flexDirection: 'column' }}>
                            <input type="text" placeholder={fm('transport.form.searchRecords', 'Kërko deponime...')} value={itemSearch} onChange={handleItemSearch}
                                style={{ ...inputStyle, marginBottom: 10, flexShrink: 0 }} />
                            <div style={{ maxHeight: 480, overflow: 'auto' }}>
                                {(props.availableItems || []).map((item: any) => {
                                    const isSelected = selectedItems.includes(item.id);
                                    return (
                                        <div key={item.id} onClick={() => toggleItem(item.id)}
                                            style={{
                                                padding: '8px 10px', borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                                                border: `1.5px solid ${isSelected ? '#1e3a5f' : '#e2e8f0'}`,
                                                background: isSelected ? '#eef3f9' : '#fff',
                                                transition: 'all 0.12s ease',
                                            }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                <div style={{
                                                    width: 16, height: 16, borderRadius: 3, marginTop: 1, flexShrink: 0,
                                                    border: `2px solid ${isSelected ? '#1e3a5f' : '#d0dbe8'}`,
                                                    background: isSelected ? '#1e3a5f' : '#fff',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    {isSelected && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{item.label || `WR-${item.id}`}</div>
                                                    <div style={{ fontSize: 11, color: '#64748b' }}>
                                                        {item.client?.name || '-'} — {item.project?.name || '-'}
                                                    </div>
                                                    {item.products && item.products.length > 0 && (
                                                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                                                            {item.products.slice(0, 3).map((p: any) => p.productName || p.articleName || p.type).filter(Boolean).join(', ')}
                                                            {item.products.length > 3 && ` +${item.products.length - 3}`}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {(!props.availableItems || props.availableItems.length === 0) && (
                                    <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 12 }}>
                                        <FormattedMessage id="transport.form.noRecords" defaultMessage="Nuk ka deponime të disponueshme" />
                                    </div>
                                )}
                            </div>
                        </div>
                        )}
                    </div>
                )}
            </div>

        </div>
    );
};

const mapStateToProps = (state: any) => ({
    currentTransport: state.transport.currentTransport,
    availableItems: state.transport.availableItems,
    loading: state.app.loading,
});

const mapDispatchToProps = (dispatch: any) => ({
    actions: bindActionCreators(transportActions as any, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TransportForm) as any) as any;
